import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    debounce,
    Divider,
    Grid,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemText,
    Skeleton,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Theme,
    Typography,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import BackNavigationButton from '../../../components/common/BackNavigationButton';
import ChatLockDurationSlider from '../../../components/common/ChatLockDurationSlider';
import LargeMaterialUISwitch from '../../../components/common/LargeMaterialUISwitch';
import OnlyFansListsSelector from '../../../components/common/OnlyFansListsSelector';
import PageContainer from '../../../components/common/PageContainter';
import StyledCard from '../../../components/common/StyledCard';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import useMessageUsersAutoCurrentChatToListStats from '../../../hooks/useMessageUsersAutoCurrentChatToListStats';
import useSextforceMessageCounters from '../../../hooks/useSextforceMessageCounters';
import useSubscriber, {
    OnlyFansMessageUsersAutoCurrentChatToListIgnoredUser,
    OnlyFansMessageUsersAutoCurrentChatToListSchema,
    OnlyFansSubscriber,
} from '../../../hooks/useSubscriber';
import useSubscriberUsernameSearchT from '../../../hooks/useSubscriberUsernameSearchT';
import { SettingsContext } from '../../../store/SettingsContext';
import { OnlyFansLists } from '../../../types/onlyFansTypes';

const StatsCard = (props: { title: string; value: number | string | React.ReactNode; isLoading: boolean }) => {
    const { title, value, isLoading } = props;
    const theme: Theme = useTheme();

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: theme.shape.borderRadius,
                borderColor: 'divider',
                height: '100%',
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            color: 'text.secondary',
                            flexGrow: 1,
                        }}
                    >
                        {title}
                    </Typography>
                    <RssFeedIcon
                        fontSize="small"
                        sx={{
                            color: 'primary.main',
                            animation: 'pulse 2s ease-in-out infinite',
                            '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.5 },
                            },
                        }}
                    />
                </Stack>
                <Typography
                    key={value ? value.toString() : '0'}
                    variant="h4"
                    sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        fontSize: '2.5rem',
                        animation: 'slideUpFade 0.4s ease-out',
                        '@keyframes slideUpFade': {
                            '0%': {
                                transform: 'translateY(20px)',
                                opacity: 0,
                            },
                            '100%': {
                                transform: 'translateY(0)',
                                opacity: 1,
                            },
                        },
                    }}
                >
                    {isLoading ? <Skeleton width={100} height={50} /> : value?.toLocaleString() || 0}
                </Typography>
            </CardContent>
        </Card>
    );
};

const secondsToHms = (d: number) => {
    // Handle negative values by taking absolute value
    const totalSeconds = Math.abs(Math.floor(d));

    // Calculate minutes and seconds
    const minutes = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;

    // Format with leading zeros
    const minutesStr = minutes.toString().padStart(2, '0');
    const secsStr = secs.toString().padStart(2, '0');

    return `${minutesStr}:${secsStr}`;
};

const ignoreUsersKey = (users: OnlyFansMessageUsersAutoCurrentChatToListIgnoredUser[]) =>
    users
        .map(u => u.id)
        .sort((a, b) => a - b)
        .join(',');

const MessageUsersAutoCurrentChatToList = () => {
    const settingsContext = useContext(SettingsContext);
    const theme: Theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();

    const [serviceActive, setServiceActive] = useState<boolean>(false);
    const [chatLockDurationMinutes, setChatLockDurationMinutes] = useState<number>(1);
    const [listId, setListId] = useState<{ id: number | string | OnlyFansLists.Type; name: string } | undefined | null>(null);
    const [listId2, setListId2] = useState<{ id: number | string | OnlyFansLists.Type; name: string } | undefined | null>(null);
    const [chatLockDurationMinutes2, setChatLockDurationMinutes2] = useState<number>(20);
    const [dontRemoveUsersFromList, setDontRemoveUsersFromList] = useState<boolean>(false);
    const [ignoreUsers, setIgnoreUsers] = useState<OnlyFansMessageUsersAutoCurrentChatToListIgnoredUser[]>([]);
    const ignoreUserIds = useMemo(() => new Set(ignoreUsers.map(u => u.id)), [ignoreUsers]);

    // Ignore list username search
    const [ignoreQueryStr, setIgnoreQueryStr] = useState<string>('');
    const [ignoreQueryStrDebounce, setIgnoreQueryStrDebounce] = useState<string>('');
    const { data: ignoreSearchResults, isLoading: ignoreSearchLoading } = useSubscriberUsernameSearchT(ignoreQueryStr);

    const ignoreSearchQueryDebounce = useCallback(debounce(setIgnoreQueryStr, 750), []);

    const ignoreSearchOnChange = (value: string) => {
        ignoreSearchQueryDebounce(value);
        setIgnoreQueryStrDebounce(value);
    };

    const addIgnoreUser = (user: OnlyFansMessageUsersAutoCurrentChatToListIgnoredUser | null) => {
        if (!user) {
            return;
        }

        setIgnoreUsers(prev => (prev.some(u => u.id === user.id) ? prev : [...prev, user]));
        ignoreSearchOnChange('');
    };

    const removeIgnoreUser = (id: number) => {
        setIgnoreUsers(prev => prev.filter(u => u.id !== id));
    };

    // Track initial values to detect changes
    const [initialValues, setInitialValues] = useState({
        serviceActive: false,
        chatLockDurationMinutes: 20,
        listId: null as { id: number | string | OnlyFansLists.Type; name: string } | undefined | null,
        listId2: null as { id: number | string | OnlyFansLists.Type; name: string } | undefined | null,
        chatLockDurationMinutes2: 20,
        dontRemoveUsersFromList: false,
        ignoreUsers: [] as OnlyFansMessageUsersAutoCurrentChatToListIgnoredUser[],
    });

    const {
        data: subscriber,
        isLoading: isSubscriberLoading,
        setServiceRunning,
        setServiceRunningLoading,
        setServiceSettings,
    } = useSubscriber();

    const { value, error } = OnlyFansMessageUsersAutoCurrentChatToListSchema.validate({
        active: serviceActive,
        chatLockDurationMinutes: chatLockDurationMinutes,
        listId: listId ? (typeof listId.id === 'number' ? listId.id : null) : null,
        listId2: listId2 ? (typeof listId2.id === 'number' ? listId2.id : null) : null,
        chatLockDurationMinutes2: listId2 ? chatLockDurationMinutes2 : null,
        dontRemoveUsersFromList: dontRemoveUsersFromList,
        ignoreUsers: ignoreUsers,
    });

    // Check if settings have been modified
    const hasChanges = useMemo(() => {
        return (
            serviceActive !== (subscriber as OnlyFansSubscriber)?.messageUsers?.autoCurrentChatToList?.active ||
            chatLockDurationMinutes !== (subscriber as OnlyFansSubscriber)?.messageUsers?.autoCurrentChatToList?.chatLockDurationMinutes ||
            listId?.id !== initialValues.listId?.id ||
            listId2?.id !== initialValues.listId2?.id ||
            (listId2 ? chatLockDurationMinutes2 !== initialValues.chatLockDurationMinutes2 : false) ||
            ignoreUsersKey(ignoreUsers) !== ignoreUsersKey(initialValues.ignoreUsers)
        );
    }, [serviceActive, chatLockDurationMinutes, listId, listId2, chatLockDurationMinutes2, ignoreUsers, initialValues]);

    useEffect(() => {
        const active = (subscriber as OnlyFansSubscriber)?.messageUsers?.autoCurrentChatToList?.active || false;
        const duration = (subscriber as OnlyFansSubscriber)?.messageUsers?.autoCurrentChatToList?.chatLockDurationMinutes || 20;
        const targetListId = (subscriber as OnlyFansSubscriber)?.messageUsers?.autoCurrentChatToList?.listId || null;
        const targetListId2 = (subscriber as OnlyFansSubscriber)?.messageUsers?.autoCurrentChatToList?.listId2 || null;
        const duration2 = (subscriber as OnlyFansSubscriber)?.messageUsers?.autoCurrentChatToList?.chatLockDurationMinutes2 || 20;
        const dontRemoveUsers = (subscriber as OnlyFansSubscriber)?.messageUsers?.autoCurrentChatToList?.dontRemoveUsersFromList || false;
        const ignored = (subscriber as OnlyFansSubscriber)?.messageUsers?.autoCurrentChatToList?.ignoreUsers || [];

        setServiceActive(active);
        setChatLockDurationMinutes(duration);
        setListId(targetListId ? { id: targetListId, name: '' } : undefined);
        setListId2(targetListId2 ? { id: targetListId2, name: '' } : undefined);
        setChatLockDurationMinutes2(duration2);
        setDontRemoveUsersFromList(dontRemoveUsers);
        setIgnoreUsers(ignored);

        // Update initial values
        setInitialValues({
            serviceActive: active,
            chatLockDurationMinutes: duration,
            listId: targetListId ? { id: targetListId, name: '' } : undefined,
            listId2: targetListId2 ? { id: targetListId2, name: '' } : undefined,
            chatLockDurationMinutes2: duration2,
            dontRemoveUsersFromList: dontRemoveUsers,
            ignoreUsers: ignored,
        });
    }, [subscriber]);

    const handleSaveSettings = async () => {
        if (error) {
            enqueueSnackbar(`Error in form: ${error.message}`, { variant: 'error', preventDuplicate: true, autoHideDuration: 3000 });
            return;
        }

        await setServiceSettings('messageUsers.autoCurrentChatToList', value)
            .then(() => {
                enqueueSnackbar(`Settings saved`, { variant: 'success', preventDuplicate: true, autoHideDuration: 2000 });

                // Update initial values after successful save
                setInitialValues({
                    serviceActive,
                    chatLockDurationMinutes,
                    listId,
                    listId2,
                    chatLockDurationMinutes2,
                    dontRemoveUsersFromList,
                    ignoreUsers,
                });
            })
            .catch(err => {
                enqueueSnackbar(`Error saving settings: ${err.message || err}`, {
                    variant: 'error',
                    preventDuplicate: true,
                    autoHideDuration: 3000,
                });
            });
    };

    // Bordered box around each group of settings to visually separate them
    const sectionSx = {
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
    };

    const { data: chatStats, isLoading: isChatStatsLoading } = useMessageUsersAutoCurrentChatToListStats();

    const { data: messageCounter, isLoading: messageCounterLoading } = useSextforceMessageCounters('fromUser', 'minute');

    return (
        <PageContainer>
            {subscriber && '_id' in subscriber && subscriber._id && (
                <>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        <BackNavigationButton url={`/subscribers/${subscriber._id}/${settingsContext.services.messageUsers.homeUrl}`} />{' '}
                        Auto Current Chat to List for {subscriber.username}
                    </Typography>
                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar title="Auto Current Chat to List" />
                    </StyledCard>
                    <StyledCard>
                        <Typography variant="body1" gutterBottom>
                            Auto Current Chat to List automatically adds users you are currently chatting with to a specified list for a set
                            duration. This allows you to easily exclude these users from your Mass DMs and Priority Messages, ensuring they
                            don't receive duplicate messages or spam in the middle of an ongoing conversation.
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Fans will be automatically removed from the list after the specified duration has passed since their last
                            message to you. If they message you again, the timer will reset and they will remain in the list for the full
                            duration from their latest message.
                        </Typography>
                    </StyledCard>
                    <StyledCard>
                        <Grid container spacing={2} flexGrow={0} alignItems="center" justifyContent="center">
                            <Grid item xs>
                                Auto Current Chat to List is currently{' '}
                                {serviceActive ? (
                                    <span style={{ color: theme.palette.success.dark }}>running</span>
                                ) : (
                                    <span style={{ color: theme.palette.error.main }}>not running</span>
                                )}
                                .
                            </Grid>
                            <Grid item xs="auto">
                                {isSubscriberLoading || setServiceRunningLoading ? (
                                    <Skeleton width={'115px'} height={'51px'} />
                                ) : (
                                    <LargeMaterialUISwitch
                                        checked={serviceActive}
                                        onChange={e => {
                                            setServiceRunning('messageUsers.autoCurrentChatToList', e.target.checked);
                                        }}
                                        color="primary"
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={sectionSx}>
                                    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 2 }}>
                                        First List
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <ChatLockDurationSlider
                                                value={chatLockDurationMinutes}
                                                onChange={setChatLockDurationMinutes}
                                                disabled={isSubscriberLoading || setServiceRunningLoading}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Stack direction={'column'} spacing={0}>
                                                <Grid container spacing={1} alignItems="center" justifyContent="center">
                                                    <Grid item xs>
                                                        <Typography variant="body1" gutterBottom>
                                                            Add users to list
                                                        </Typography>
                                                        <Typography variant="body2" gutterBottom color="textSecondary">
                                                            Select the list to which you want to add users, or create a new one.
                                                        </Typography>
                                                    </Grid>
                                                </Grid>

                                                <OnlyFansListsSelector
                                                    size="small"
                                                    includeBuiltInLists={false}
                                                    targetList={listId}
                                                    setTargetList={setListId}
                                                    showCreateListButton
                                                />
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={sectionSx}>
                                    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 2 }}>
                                        Second List (Optional)
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Stack direction={'column'} spacing={0}>
                                                <Grid container spacing={1} alignItems="center" justifyContent="center">
                                                    <Grid item xs>
                                                        <Typography variant="body1" gutterBottom>
                                                            Also add users to a second list
                                                        </Typography>
                                                        <Typography variant="body2" gutterBottom color="textSecondary">
                                                            Optionally select a second list to which users will also be added, with its own
                                                            duration. Leave empty to disable.
                                                        </Typography>
                                                    </Grid>
                                                </Grid>

                                                <OnlyFansListsSelector
                                                    size="small"
                                                    includeBuiltInLists={false}
                                                    includeSelectNoneOption
                                                    targetList={listId2}
                                                    setTargetList={setListId2}
                                                    showCreateListButton
                                                />
                                            </Stack>
                                        </Grid>
                                        {listId2 && (
                                            <Grid item xs={12}>
                                                <ChatLockDurationSlider
                                                    value={chatLockDurationMinutes2}
                                                    onChange={setChatLockDurationMinutes2}
                                                    disabled={isSubscriberLoading || setServiceRunningLoading}
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={sectionSx}>
                                    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 2 }}>
                                        List Removal
                                    </Typography>
                                    <Grid container spacing={1} flexGrow={1} alignItems="flex-start">
                                        <Grid item xs>
                                            <Stack direction={'column'} spacing={0}>
                                                <Typography variant="body1">Don't remove users from list automatically</Typography>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    If enabled, users added to the list will remain there until you manually remove them.
                                                    Perfect if you prefer to clear the list manually every shift, for example. Please note
                                                    that this may lead to a buildup of users in the list over time. Also, you will not see
                                                    the tracked chats stats below as they depend on automatic removal.
                                                </Typography>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs="auto">
                                            <Switch
                                                size="medium"
                                                checked={dontRemoveUsersFromList}
                                                disabled={isSubscriberLoading || setServiceRunningLoading}
                                                onChange={e => {
                                                    setDontRemoveUsersFromList(e.target.checked);
                                                }}
                                                color="primary"
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={sectionSx}>
                                    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 2 }}>
                                        Ignore Users (Optional)
                                    </Typography>
                                    <Stack direction="column" spacing={1}>
                                        <Stack direction="column" spacing={0}>
                                            <Typography variant="body1">Never add these users to the lists</Typography>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Search for fans who should be skipped by this service. Users on this list will not be added
                                                to either list when they message you, so your Mass DMs and Priority Messages will still
                                                reach them. Remember to save your changes.
                                            </Typography>
                                        </Stack>
                                        <Autocomplete<OnlyFansMessageUsersAutoCurrentChatToListIgnoredUser, false, false, false>
                                            size="small"
                                            options={
                                                ignoreSearchResults?.map(item => ({
                                                    id: item.id,
                                                    username: item.username,
                                                })) ?? []
                                            }
                                            loading={ignoreSearchLoading}
                                            disabled={isSubscriberLoading || setServiceRunningLoading}
                                            value={null}
                                            inputValue={ignoreQueryStrDebounce}
                                            getOptionLabel={opt => opt.username}
                                            filterOptions={x => x}
                                            isOptionEqualToValue={(a, b) => a.id === b.id}
                                            onInputChange={(_, newValue) => ignoreSearchOnChange(newValue)}
                                            onChange={(_, newValue) => addIgnoreUser(newValue)}
                                            getOptionDisabled={option => ignoreUserIds.has(option.id)}
                                            renderOption={(props, option) => {
                                                const alreadyAdded = ignoreUserIds.has(option.id);
                                                const name = ignoreSearchResults?.find(r => r.id === option.id)?.name;

                                                return (
                                                    <li {...props} key={option.id}>
                                                        <Box
                                                            sx={{
                                                                width: '100%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                            }}
                                                        >
                                                            <Typography variant="body2">
                                                                {name ? `(${name}) ` : ''}
                                                                {option.username}
                                                            </Typography>
                                                            {alreadyAdded && (
                                                                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                                                    Added
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </li>
                                                );
                                            }}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    label="Search"
                                                    placeholder="Type a username to search..."
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {ignoreSearchLoading ? (
                                                                    <CircularProgress color="inherit" size={18} />
                                                                ) : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                        {ignoreUsers.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No users are being ignored
                                            </Typography>
                                        ) : (
                                            <List dense disablePadding sx={{ maxHeight: 300, overflow: 'auto' }}>
                                                {ignoreUsers.map((user, index) => (
                                                    <Fragment key={user.id}>
                                                        <ListItem
                                                            disableGutters
                                                            secondaryAction={
                                                                <IconButton
                                                                    aria-label="remove"
                                                                    size="small"
                                                                    color="error"
                                                                    disabled={isSubscriberLoading || setServiceRunningLoading}
                                                                    onClick={() => removeIgnoreUser(user.id)}
                                                                >
                                                                    <CloseIcon fontSize="small" />
                                                                </IconButton>
                                                            }
                                                        >
                                                            <ListItemText
                                                                primary={
                                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                                        <Typography variant="body2">{user.username}</Typography>
                                                                        <IconButton
                                                                            size="small"
                                                                            LinkComponent={Link}
                                                                            href={`https://onlyfans.com/${user.username}`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            title={`View ${user.username}'s profile on OnlyFans`}
                                                                            disableFocusRipple
                                                                            disableRipple
                                                                            sx={{ m: 0, p: 0 }}
                                                                        >
                                                                            <OpenInNewIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Stack>
                                                                }
                                                            />
                                                        </ListItem>
                                                        {index < ignoreUsers.length - 1 && <Divider />}
                                                    </Fragment>
                                                ))}
                                            </List>
                                        )}
                                    </Stack>
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Divider />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                                    <Button
                                        variant="contained"
                                        color={hasChanges ? 'warning' : 'primary'}
                                        fullWidth
                                        onClick={handleSaveSettings}
                                        disabled={isSubscriberLoading || setServiceRunningLoading || !!error || !listId}
                                        size="large"
                                    >
                                        {hasChanges ? 'Save Changes' : 'Save Settings'}
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </StyledCard>

                    {/* Stats */}
                    <StyledCard noCard>
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={6}>
                                <StatsCard
                                    title="Messages Received in the Last Minute"
                                    value={messageCounter?.count || 0}
                                    isLoading={messageCounterLoading}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <StatsCard
                                    title="Active Chats Being Tracked"
                                    value={chatStats?.totalActive || 0}
                                    isLoading={isChatStatsLoading}
                                />
                            </Grid>
                        </Grid>
                    </StyledCard>

                    {/* Chat List */}
                    <StyledCard noCard noBottomMargin>
                        <StyledCardTitleBar
                            title={`Tracked Chats (${chatStats?.totalActive.toLocaleString() || 0})`}
                            isLoading={isChatStatsLoading}
                        />
                    </StyledCard>
                    <StyledCard>
                        <Stack direction="column" spacing={2}>
                            <div
                                style={{
                                    overflowX: 'auto',
                                    width: '100%',
                                }}
                            >
                                <Table
                                    size="small"
                                    sx={{
                                        // No padding on the left and right of the table but only on the first and last cell
                                        '& td': {
                                            '&:first-of-type': {
                                                paddingLeft: '0px',
                                                paddingRight: '0px',
                                            },
                                            '&:last-child': {
                                                paddingRight: '0px',
                                            },
                                        },
                                        '& th': {
                                            fontWeight: 'bold',
                                            '&:first-of-type': {
                                                paddingLeft: '0px',
                                                paddingRight: '0px',
                                            },
                                            '&:last-child': {
                                                paddingRight: '0px',
                                            },
                                        },
                                        // Remove the bordder from the cells in the last row
                                        '& tr:last-child td': {
                                            borderBottom: 'none',
                                        },
                                    }}
                                    width="100%"
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Username</TableCell>
                                            <TableCell width="140px">Last Message At</TableCell>
                                            <TableCell width="80px">Min. Left</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {chatStats?.chats.map(chat => (
                                            <TableRow key={chat.fanId}>
                                                <TableCell>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <Typography variant="body2">{chat.fanUsername || chat.fanId}</Typography>
                                                        {chat.fanUsername && (
                                                            <IconButton
                                                                size="small"
                                                                LinkComponent={Link}
                                                                href={`https://onlyfans.com/${chat.fanUsername}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title={`View ${chat.fanUsername}'s profile on OnlyFans`}
                                                                disableFocusRipple
                                                                disableRipple
                                                                sx={{
                                                                    m: 0,
                                                                    p: 0,
                                                                }}
                                                            >
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {moment(chat.lastActivity).format('h:mm:ss a')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {secondsToHms(chat.expiresIn)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </Stack>
                    </StyledCard>
                </>
            )}
        </PageContainer>
    );
};

export default MessageUsersAutoCurrentChatToList;
